// src/components/ChapterTemplate.js
import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import RealPianoListener from "./RealPianoListener";
import VirtualPiano from "./VirtualPiano";
import Confetti from "react-confetti";
import { addSkillPoints } from "../utils/skill";
import DoronCompanion from "./DoronCompanion";

import StarCounter from "./StarCounter";
import StarGazerGizModal from "./StarGazerGizModal";
import LetterRevealModal from "./LetterRevealModal";
import OverworldLightbox from "./OverworldLightbox";
import PianoViewport from "./PianoViewport";
import ManaStarMeter from "./ManaStarMeter";
import PianoKeyMapCard from "./PianoKeyMapCard";

// Paywall gating
import { isSubscribed } from "../utils/subscription";
import Paywall from "./Paywall";

import {
  setChapterStars,
  getTotalStars,
  goalStars,
  hasSeenGizReveal,
} from "../utils/stars";
import { starChapters } from "../data/starQuest";

/* Left sidebar list (unchanged) */
const chapters = [
  { number: 1, title: "Dragon Sleep", path: "/chapter-1" },
  { number: 2, title: "Zeb’s Birthday", path: "/chapter-2" },
  { number: 3, title: "Smile Mr. Peng", path: "/chapter-3" },
  { number: 4, title: "Melody March", path: "/chapter-4" },
  { number: 5, title: "Rudolph’s Ride", path: "/chapter-5" },
  { number: 6, title: "Spider Dance", path: "/chapter-6" },
  { number: 7, title: "Panda Parade", path: "/chapter-7" },
  { number: 8, title: "Anthem Awakens", path: "/chapter-8" },
  { number: 9, title: "Crocodile Chorus", path: "/chapter-9" },
  { number: 10, title: "Für Elise Finale", path: "/chapter-10" },
];

/** Right-rail mini map */
function MiniRoadmapThumb({ onOpen }) {
  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen?.();
    }
  };
  return (
    <div className="mt-3">
      <div
        className="relative rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm cursor-pointer"
        onClick={onOpen}
        onKeyDown={handleKey}
        role="button"
        tabIndex={0}
        aria-label="Open Treble Town™ map"
        title="Open map"
      >
        <div className="relative w-full pb-[62.5%]">
          <img
            src="/images/map/overworld.png"
            alt="Treble Town™ Map"
            className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
            draggable="false"
          />
          <div className="absolute right-2 top-2 z-[2] rounded-md bg-white/90 backdrop-blur px-2 py-[2px] text-[11px] shadow">
            ↗︎
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-600">
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-green-300 inline-block" />
          Collected
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-yellow-300 inline-block" />
          Next stop
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-3 rounded-full bg-gray-300 inline-block" />
          Locked
        </span>
      </div>
    </div>
  );
}

/** Right rail widget */
function SkillRailWidget({
  title = "Skill & Progress",
  progress = 20,
  stars = 2,
  goal = 8,
  onOpenMap,
}) {
  return (
    <div className="mt-3 rounded-xl border border-purple-200 bg-white/90 shadow-sm p-3">
      <div className="text-xs font-bold text-purple-800">{title}</div>

      <div className="mt-2 text-[12px] text-gray-700 flex items-center gap-1">
        <span>⭐</span>
        <span className="font-semibold">{stars}</span>
        <span>/ {goal}</span>
      </div>

      <div className="mt-2">
        <div className="h-2 w-full rounded-full bg-purple-100 overflow-hidden">
          <div
            className="h-2 bg-purple-400"
            style={{ width: `${Math.max(0, Math.min(100, progress))}%` }}
          />
        </div>
        <div className="mt-1 text-[11px] text-gray-500">
          Skill Level: {progress} / 100
        </div>
      </div>

      <MiniRoadmapThumb onOpen={onOpenMap} />
    </div>
  );
}

/** Companions column */
function CompanionsCompact({ companionsText }) {
  return (
    <div className="hidden lg:flex flex-col gap-4 pt-1 w-[120px]">
      <div className="flex flex-col items-center text-center">
        <img src="/images/wis.png" alt="Wis" className="w-[52px] h-auto object-contain" />
        <div className="mt-1 text-[11px] text-gray-700 leading-tight max-w-[110px]">
          {companionsText.wis || "Focus and listen closely!"}
        </div>
      </div>
      <div className="flex flex-col items-center text-center">
        <img src="/images/pan-the-panda.png" alt="Pan" className="w-[62px] h-auto object-contain" />
        <div className="mt-1 text-[11px] text-gray-700 leading-tight max-w-[110px]">
          {companionsText.pan || "Ready? Let’s play the melody!"}
        </div>
      </div>
    </div>
  );
}

export default function ChapterTemplate({
  chapterNumber,
  chapterTitle,
  storySlides,
  targetMelody,
  melodyLines,
  sceneImageSrc,
  companionsText = {},
  matchedNotes = [],         // optional external control
  onNoteMatched = () => {},
  onRealComplete = () => {},
  onVirtualComplete = () => {},
  unlockNextChapterPath,
  skillPoints = 5,
  chapterId: chapterIdProp,
  layoutMode = "cinema",
  compactSidebar = true,
  ctaLabel = "▶️ PLAY NOW",
  songTitle,
  doronAwakeInitially = false,
  contentClassName = "",
  // ⬆️ BIGGER story slides (consistent across chapters)
  slideHeightClass = "h-[60vh] sm:h-[64vh] lg:h-[66vh]",
  storyCardMaxWidthClass = "max-w-[1200px]",
  // game view sizes
  sceneHeightClass = "h-[260px] sm:h-[280px]",
  maxWidthClass = "max-w-[1150px]",
  centerWidthClass = "max-w-[720px]",
  rightWidthClass = "w-[260px]",
}) {
  const navigate = useNavigate();
  const chapterId = chapterIdProp || `chapter-${chapterNumber}`;

  // ---------------------------
  // All hooks must come first 👇
  // ---------------------------

  const [currentSlide, setCurrentSlide] = useState(0);
  const [showGame, setShowGame] = useState(false);

  const [virtualDone, setVirtualDone] = useState(false);
  const [realDone, setRealDone] = useState(false);
  const [skillAdded, setSkillAdded] = useState(false);

  const [letterOpen, setLetterOpen] = useState(false);
  const [gizOpen, setGizOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);

  const playAnchorRef = useRef(null);

  // Derived melody (flatten + filter)
  const safeSlides = useMemo(() => (Array.isArray(storySlides) ? storySlides : []), [storySlides]);
  const baseMelody = useMemo(() => (Array.isArray(targetMelody) ? targetMelody : []), [targetMelody]);

  const playMelody = useMemo(() => {
    if (Array.isArray(melodyLines)) {
      const isNote = (t) => t && String(t).trim() !== "" && t !== "__";
      return melodyLines.flat().filter(isNote);
    }
    return baseMelody;
  }, [melodyLines, baseMelody]);

  // total tokens for progress
  const totalTokens = useMemo(
    () => (Array.isArray(playMelody) ? playMelody.filter((t) => t && String(t).trim() !== "" && t !== "__").length : 0),
    [playMelody]
  );

  useEffect(() => {
    if (!safeSlides.length) console.warn(`[ChapterTemplate] No storySlides for chapter ${chapterNumber}.`);
    if (!playMelody.length) console.warn(`[ChapterTemplate] No melody sequence for chapter ${chapterNumber}.`);
  }, [safeSlides.length, playMelody.length, chapterNumber]);

  const allCompleted = virtualDone || realDone;

  // 🔔 FINAL redirect & one-time award handling
  useEffect(() => {
    if (!allCompleted) return;

    // One-time skill/stars grant
    if (!skillAdded) {
      setSkillAdded(true);
      addSkillPoints(skillPoints);

      // ✅ mark this chapter complete for routing guards
      try { localStorage.setItem(`tt_ch${chapterNumber}_done`, "1"); } catch {}

      if (typeof chapterNumber === "number" && chapterNumber >= 2) {
        setChapterStars(chapterNumber, 1);
        setChapterStars(chapterId, 1);
        const starInfo = starChapters[chapterId];
        if (starInfo) setChapterStars(chapterId, 1);
      }
    }

    // Finale: skip modals and jump straight to /victory
    if (Number(chapterNumber) === 10) {
      setLetterOpen(false);
      setGizOpen(false);
      navigate("/victory", { replace: true });
      return;
    }

    // Non-finale: show letter modal as before (only once)
    setLetterOpen(true);
  }, [allCompleted, skillAdded, skillPoints, chapterNumber, chapterId, navigate]);

  // Giz reveal only when not on the finale
  useEffect(() => {
    if (!allCompleted) return;
    if (Number(chapterNumber) === 10) return; // never open on Ch10
    const total = getTotalStars();
    if (total >= goalStars() && !hasSeenGizReveal()) setGizOpen(true);
  }, [allCompleted, chapterNumber]);

  useEffect(() => {
    if (showGame) {
      const id = setTimeout(() => {
        playAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 60);
      return () => clearTimeout(id);
    }
  }, [showGame]);

  // PROGRESS / HIGHLIGHT STATE
  const [internalMatchedCount, setInternalMatchedCount] = useState(0);
  const [vpProgress, setVpProgress] = useState(0);
  const [vpLastCorrect, setVpLastCorrect] = useState(null); // null | true | false

  const highlightCount = Math.max(
    internalMatchedCount,
    Array.isArray(matchedNotes) ? matchedNotes.length : 0
  );

  const renderMelody = (matchedCount) => {
    if (Array.isArray(melodyLines)) {
      let globalIdx = 0;
      const isSpacer = (t) => !t || t === "__" || String(t).trim() === "";
      return (
        <div className="flex flex-col items-center gap-1">
          {melodyLines.map((line, i) => (
            <div key={`line-${i}`} className="flex flex-wrap justify-center gap-2">
              {line.map((token, idx) => {
                if (isSpacer(token)) return <span key={`gap-${i}-${idx}`} className="inline-block mx-3" />;
                const isMatched = globalIdx < matchedCount;
                const el = (
                  <span
                    key={`${token}-${i}-${idx}`}
                    className={`rounded px-2 py-1 text-[15px] sm:text-base font-semibold transition-colors ${
                      isMatched ? "bg-green-300 text-green-900" : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {token}
                  </span>
                );
                globalIdx += 1;
                return el;
              })}
            </div>
          ))}
        </div>
      );
    }
    // simple mode
    return (
      <div className="flex justify-center flex-wrap gap-2">
        {playMelody.map((note, idx) => {
          const isMatched = idx < matchedCount;
          return (
            <span
              key={`${note}-${idx}`}
              className={`rounded px-3 py-1 text-base sm:text-lg font-semibold transition-colors ${
                isMatched ? "bg-green-300 text-green-900" : "bg-gray-100 text-gray-800"
              }`}
            >
              {note}
            </span>
          );
        })}
      </div>
    );
  };

  const sceneSrc =
    sceneImageSrc ||
    safeSlides.find((s) => s.melody && s.image)?.image ||
    safeSlides[safeSlides.length - 1]?.image ||
    null;

  const doronStartsAwake = chapterNumber === 1 || doronAwakeInitially;
  const doronIsAsleep = doronStartsAwake ? (virtualDone || realDone) : true;

  // Piano Key Map: expected note (current target)
  const expectedNote = useMemo(() => {
    if (!Array.isArray(playMelody) || playMelody.length === 0) return null;
    const idx = Math.min(internalMatchedCount, playMelody.length - 1);
    return playMelody[idx];
  }, [internalMatchedCount, playMelody]);

  // compact meter value
  const meterValue = Math.min(
    totalTokens,
    Math.max(internalMatchedCount, Array.isArray(matchedNotes) ? matchedNotes.length : 0)
  );

  // ---------------------------
  // Paywall decision AFTER hooks
  // ---------------------------
  const locked = Number(chapterNumber) >= 4 && !isSubscribed();
  if (locked) {
    return <Paywall chapterNumber={chapterNumber} />;
  }

  // ---------------------------
  // Normal render
  // ---------------------------
  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-purple-100 flex relative">
      <StarCounter />
      {allCompleted && <Confetti />}

      {/* LEFT SIDEBAR */}
      <aside className={`${compactSidebar ? "w-16" : "w-24"} bg-white/90 hidden md:flex flex-col items-center py-3 space-y-2 border-r border-gray-200`}>
        <button onClick={() => navigate("/")} className="text-blue-600 font-extrabold underline text-xs" title="Home">
          HOME
        </button>
        <div className="text-gray-700 font-bold mt-1 text-xs">Chp</div>
        {chapters.map((ch) => (
          <div
            key={ch.number}
            title={ch.title}
            className={`w-7 h-7 flex items-center justify-center rounded-full shadow text-[11px] font-bold ${
              ch.number === chapterNumber
                ? "bg-purple-300 text-purple-900"
                : ch.number < chapterNumber
                ? "bg-green-300 text-green-800"
                : "bg-gray-200 text-gray-500"
            }`}
          >
            {ch.number}
          </div>
        ))}
      </aside>

      {/* MAIN COLUMN */}
      <div className="flex-1 flex flex-col items-center p-2 sm:p-3">
        <div className={`w-full ${maxWidthClass} mx-auto bg-white/95 rounded-xl shadow-md px-3 sm:px-4 py-3 sm:py-4 relative ${contentClassName || "min-h-[62vh] md:min-h[70vh]"}`}>
          {/* STORY FIRST */}
          {!showGame ? (
            <div className="flex flex-col items-center">
              {safeSlides.length ? (
                <div className="w-full mx-auto">
                  {/* Story card */}
                  <div className={`mx-auto w-full ${storyCardMaxWidthClass}`}>
                    <div className="relative w-full rounded-2xl shadow overflow-hidden border border-gray-100">
                      <div className={`w-full ${slideHeightClass}`}>
                        {safeSlides[currentSlide]?.image && (
                          <img
                            src={safeSlides[currentSlide].image}
                            alt=""
                            className="w-full h-full object-contain"
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  {safeSlides[currentSlide]?.text && (
                    <p className="text-center mt-2 text-sm">{safeSlides[currentSlide].text}</p>
                  )}

                  <div className="flex justify-center gap-3 mt-3">
                    <button
                      onClick={() => setCurrentSlide((s) => Math.max(0, s - 1))}
                      disabled={currentSlide === 0}
                      className="px-4 py-2 rounded border bg-white shadow disabled:opacity-50 text-base"
                    >
                      ◀ Prev
                    </button>
                    <button
                      onClick={() => setCurrentSlide((s) => Math.min(safeSlides.length - 1, s + 1))}
                      disabled={currentSlide === safeSlides.length - 1}
                      className="px-4 py-2 rounded border bg-white shadow disabled:opacity-50 text-base"
                    >
                      Next ▶
                    </button>
                  </div>

                  <div className="flex justify-center">
                    <button
                      onClick={() => setShowGame(true)}
                      className="mt-3 bg-yellow-400 py-3 px-8 rounded-full shadow-md font-semibold text-base"
                    >
                      Start Game
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center text-sm">No slides found.</div>
              )}
            </div>
          ) : (
            /* GAME VIEW */
            <div className="flex flex-col lg:flex-row lg:items-start gap-3 sm:gap-4">
              {/* Left companions + compact meter */}
              <div className="hidden lg:flex flex-col items-center w-[120px]">
                <CompanionsCompact companionsText={companionsText} />
                <div className="mt-2 w-full flex justify-center">
                  <ManaStarMeter
                    value={meterValue}
                    max={totalTokens}
                    height={180}
                    width={46}
                    showLabel={false}
                    starSize={22}
                  />
                </div>
              </div>

              {/* CENTER */}
              <div ref={playAnchorRef} className={`flex-1 lg:flex-none ${centerWidthClass} mx-auto lg:mx-0`}>
                {/* Scene */}
                <div className="w-full rounded-lg overflow-hidden border border-gray-100">
                  <div className={`relative w-full ${sceneHeightClass}`}>
                    {sceneSrc && <img src={sceneSrc} alt="Scene" className="absolute inset-0 w-full h-full object-contain" />}
                  </div>
                </div>

                {/* Melody bubbles */}
                <div className="mt-3 w-full bg-white rounded-xl shadow px-5 py-3 border border-gray-100">
                  <div className="text-center font-bold mb-2 text-sm sm:text-base">{songTitle || "Melody Notes"}</div>
                  {renderMelody(highlightCount)}
                </div>

                {/* Real piano */}
                <RealPianoListener
                  targetMelody={playMelody}
                  onNoteMatched={(arr) => {
                    setInternalMatchedCount(arr.length);
                    onNoteMatched(arr);
                  }}
                  onComplete={() => {
                    onRealComplete();
                    setRealDone(true);
                  }}
                  startLabel="Play Now"
                  stopLabel="Stop Listening"
                  buttonWrapperClassName="flex justify-center mt-3 mb-2"
                  buttonClassName="text-white font-extrabold text-lg sm:text-xl rounded-xl px-8 sm:px-10 py-3 shadow active:scale-[0.99] bg-[#1EEA3B]"
                />

                {/* Virtual piano */}
                <h3 className="text-center font-extrabold tracking-wide text-purple-700 mt-3 mb-2 text-xl sm:text-2xl">
                  🎹 VIRTUAL PIANO
                </h3>

                <div className="w-full flex justify-center overflow-x-auto">
                  <div className="min-w-[320px] max-w-[720px] w-full">
                    <PianoViewport naturalWidth={720} naturalHeight={240} desktopBreakpoint={760} narrowPortrait={420}>
                      <div className="flex justify-center">
                        <VirtualPiano
                          targetMelody={playMelody}
                          onProgress={(index, total, wasCorrect) => {
                            setVpProgress(index);
                            setVpLastCorrect(wasCorrect);
                            setInternalMatchedCount(index);
                          }}
                          onComplete={() => {
                            onVirtualComplete();
                            setVirtualDone(true);
                          }}
                        />
                      </div>
                    </PianoViewport>
                  </div>
                </div>

                {/* Progress line */}
                <div className="text-center text-sm text-gray-600 mt-2">
                  <span className="inline-flex items-center gap-2">
                    <span>Progress: {vpProgress}/{totalTokens}</span>
                    {vpLastCorrect === true && <span className="text-green-600 font-bold">✓</span>}
                    {vpLastCorrect === false && <span className="text-red-600 font-bold">✗</span>}
                  </span>
                </div>

                {/* Unlock next chapter (never on finale) */}
                {(virtualDone || realDone) && unlockNextChapterPath && Number(chapterNumber) !== 10 && (
                  <div className="mt-2 text-center">
                    <button onClick={() => navigate(unlockNextChapterPath)} className="bg-green-500 text-white py-2 px-5 rounded-full shadow text-sm">
                      ✅ Unlock Chapter {chapterNumber + 1}
                    </button>
                  </div>
                )}
              </div>

              {/* RIGHT rail */}
              <div className={`lg:ml-auto ${rightWidthClass} shrink-0`}>
                <div className="sticky top-3">
                  <div className="rounded-xl border border-gray-200 bg-white/95 p-3 shadow-sm">
                    <div className="flex justify-end">
                      <DoronCompanion isAsleep={doronIsAsleep} />
                    </div>
                  </div>

                  <SkillRailWidget
                    progress={Math.min(100, (getTotalStars() / goalStars()) * 100)}
                    stars={getTotalStars()}
                    goal={goalStars()}
                    onOpenMap={() => setMapOpen(true)}
                  />

                  {/* Piano Keys Map (shows current expected note) */}
                  <PianoKeyMapCard
                    className="mt-3"
                    expectedNote={expectedNote}
                    caption="PIANO KEYS KEY MAP"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="text-center text-[11px] text-gray-500 mt-2">
          © 2025 Treble Town™ • A musical journey by Nishat Chadha
        </div>
      </div>

      {/* Modals */}
      <LetterRevealModal
        open={letterOpen}
        onClose={() => setLetterOpen(false)}
        onNext={unlockNextChapterPath ? () => navigate(unlockNextChapterPath) : undefined}
        nextButtonText={chapterNumber === 1 ? "Go to Chapter 2" : `Go to Chapter ${chapterNumber + 1}`}
        chapter={chapterNumber}
        mode={chapterNumber === 1 ? "intro" : "chapter"}
        companions={{
          left: { name: "Pan", avatar: "/images/pan.png" },
          right: { name: "Wis", avatar: "/images/wis.png" },
        }}
        currentStopIndex={Math.max(0, chapterNumber - 1)}
      />

      <StarGazerGizModal
        open={gizOpen}
        onClose={() => setGizOpen(false)}
        onGoToFinal={() => navigate("/chapter-10")}
      />

      <OverworldLightbox
        open={mapOpen}
        onClose={() => setMapOpen(false)}
        currentChapter={chapterNumber}
        hasPremium={isSubscribed()}
        onLocked={() => alert("Chapters 4–10 are premium. Please subscribe to unlock!")}
      />
    </div>
  );
}
